import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { LibraryBig, Search, ArrowLeft } from "lucide-react";
import { GLOSSARY_TERMS } from "@/data/learn/glossary";
import { BookmarkButton } from "@/components/learn/BookmarkButton";

export const Route = createFileRoute("/learn/glossary")({
  head: () => ({
    meta: [
      { title: "Trauma Glossary A–Z — Recovery Trauma™" },
      { name: "description", content: "A compassionate A–Z of trauma-related terms — from attachment and dissociation to the vagus nerve and the window of tolerance. Plain English, searchable." },
      { property: "og:title", content: "Trauma Glossary A–Z — Recovery Trauma™" },
      { property: "og:description", content: "Plain-English definitions of trauma-related concepts, written with compassion." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://recoverytrauma.com/learn/glossary" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://recoverytrauma.com/learn/glossary" }],
  }),
  component: GlossaryPage,
});

function slugify(t: string) {
  return t.replace(/[^\w]+/g, "-");
}

const TERM_TONES = [
  { card: "bg-gradient-to-br from-[#eef8ef] to-[#dcefde] border-emerald-200/60 dark:from-emerald-950/60 dark:to-emerald-900/40 dark:border-emerald-800/50", chip: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-100" },
  { card: "bg-gradient-to-br from-[#f4efff] to-[#e9deff] border-violet-200/60 dark:from-violet-950/60 dark:to-violet-900/40 dark:border-violet-800/50", chip: "bg-violet-100 text-violet-800 dark:bg-violet-900/60 dark:text-violet-100" },
  { card: "bg-gradient-to-br from-[#eef6ff] to-[#dcebff] border-sky-200/60 dark:from-sky-950/60 dark:to-sky-900/40 dark:border-sky-800/50", chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/60 dark:text-sky-100" },
  { card: "bg-gradient-to-br from-[#fff2df] to-[#ffe1bd] border-orange-200/60 dark:from-orange-950/60 dark:to-orange-900/40 dark:border-orange-800/50", chip: "bg-orange-100 text-orange-800 dark:bg-orange-900/60 dark:text-orange-100" },
  { card: "bg-gradient-to-br from-[#fff1f4] to-[#fde3ea] border-rose-200/60 dark:from-rose-950/60 dark:to-rose-900/40 dark:border-rose-800/50", chip: "bg-rose-100 text-rose-800 dark:bg-rose-900/60 dark:text-rose-100" },
  { card: "bg-gradient-to-br from-[#e6f8f4] to-[#cfeee8] border-teal-200/60 dark:from-teal-950/60 dark:to-teal-900/40 dark:border-teal-800/50", chip: "bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-100" },
  { card: "bg-gradient-to-br from-[#fdf7e4] to-[#faf3d4] border-amber-200/60 dark:from-amber-950/60 dark:to-amber-900/40 dark:border-amber-800/50", chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-100" },
] as const;


function toneForLetter(L: string) {
  const idx = (L.charCodeAt(0) - 65 + 7) % TERM_TONES.length;
  return TERM_TONES[Math.max(0, idx)];
}

function GlossaryPage() {
  const [query, setQuery] = useState("");
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const q = query.trim().toLowerCase();

  const searchFiltered = useMemo(
    () =>
      GLOSSARY_TERMS.filter(
        (t) => !q || t.term.toLowerCase().includes(q) || t.short.toLowerCase().includes(q),
      ).sort((a, b) => a.term.localeCompare(b.term)),
    [q],
  );

  const availableLetters = useMemo(() => {
    const s = new Set(searchFiltered.map((t) => t.term[0].toUpperCase()));
    return Array.from(s).sort();
  }, [searchFiltered]);

  const filtered = useMemo(
    () =>
      activeLetter
        ? searchFiltered.filter((t) => t.term[0].toUpperCase() === activeLetter)
        : searchFiltered,
    [searchFiltered, activeLetter],
  );

  const letters = useMemo(() => {
    const s = new Set(filtered.map((t) => t.term[0].toUpperCase()));
    return Array.from(s).sort();
  }, [filtered]);

  return (
    <main className="min-h-dvh bg-gradient-to-b from-amber-50/60 via-background to-background px-4 py-10">
     <div className="mx-auto max-w-4xl">
      <Link to="/learn" className="mb-6 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to Learn
      </Link>

      <header className="mb-8">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <LibraryBig className="h-3.5 w-3.5" /> Glossary
        </div>
        <h1 className="font-serif text-4xl tracking-tight text-foreground md:text-5xl">
          Trauma glossary, in plain English
        </h1>
        <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted-foreground">
          These are the words we use across Recovery Trauma™, defined with
          compassion. They're not clinical descriptions — they're a starting
          point for understanding yourself with more kindness.
        </p>
        <div className="mt-4">
          <BookmarkButton id="learn:glossary" title="Trauma Glossary" href="/learn/glossary" section="Learn" />
        </div>
      </header>

      <div className="relative mb-6">
        <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search terms…"
          aria-label="Search glossary terms"
          className="w-full rounded-full border border-border bg-card py-3 ps-11 pe-4 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {availableLetters.length > 0 && (
        <nav aria-label="Filter by letter" className="mb-8 flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setActiveLetter(null)}
            aria-pressed={activeLetter === null}
            className={`inline-flex h-8 items-center rounded-full border px-3 text-xs font-medium transition-colors ${
              activeLetter === null
                ? "border-gold bg-gold text-white"
                : "border-border bg-card text-foreground/75 hover:border-gold hover:text-gold"
            }`}
          >
            All
          </button>
          {availableLetters.map((L) => {
            const isActive = activeLetter === L;
            const tone = toneForLetter(L);
            return (
              <button
                key={L}
                type="button"
                onClick={() => setActiveLetter(isActive ? null : L)}
                aria-pressed={isActive}
                className={`inline-flex h-9 w-9 items-center justify-center rounded-full border text-sm font-semibold transition-transform hover:scale-110 ${
                  isActive
                    ? "border-gold bg-gold text-white shadow-md"
                    : `border-transparent ${tone.chip} hover:shadow-sm`
                }`}
              >
                {L}
              </button>
            );
          })}
        </nav>
      )}



      {filtered.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card p-6 text-center text-sm text-muted-foreground">
          No terms {activeLetter ? `starting with "${activeLetter}"` : ""}{q ? ` match "${query}"` : ""}. Try another letter or search.
        </p>

      ) : (
        <div className="space-y-8">
          {letters.map((L) => {
            const items = filtered.filter((t) => t.term[0].toUpperCase() === L);
            const tone = toneForLetter(L);
            return (
              <section key={L} id={`letter-${L}`} aria-labelledby={`heading-${L}`}>
                <h2 id={`heading-${L}`} className="mb-4 flex items-center gap-3 font-serif text-2xl text-foreground">
                  <span className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl font-bold shadow-sm ${tone.chip}`}>{L}</span>
                  <span className="h-px flex-1 bg-border" />
                </h2>
                <ul className="grid gap-4 sm:grid-cols-2">
                  {items.map((t, i) => {
                    const cardTone = TERM_TONES[(L.charCodeAt(0) + i) % TERM_TONES.length];
                    return (
                      <li
                        key={t.term}
                        id={slugify(t.term)}
                        className={`rounded-2xl border p-5 shadow-sm scroll-mt-24 ${cardTone.card}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <h3 className="font-serif text-lg text-foreground">{t.term}</h3>
                          <BookmarkButton
                            variant="icon"
                            id={`glossary:${slugify(t.term)}`}
                            title={t.term}
                            href={`/learn/glossary#${slugify(t.term)}`}
                            section="Glossary"
                          />
                        </div>
                        <p className="mt-1.5 text-sm leading-relaxed text-foreground/75">{t.short}</p>
                        {t.related && t.related.length > 0 && (
                          <p className="mt-3 flex flex-wrap gap-1.5 text-xs">
                            <span className="text-foreground/60">Related:</span>
                            {t.related.map((r) => (
                              <a
                                key={r}
                                href={`#${slugify(r)}`}
                                className="rounded-full bg-white/70 px-2 py-0.5 text-foreground/70 hover:bg-white hover:text-foreground dark:bg-black/30 dark:hover:bg-black/50"
                              >
                                {r}
                              </a>
                            ))}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
      )}

      <p className="mt-10 rounded-2xl border border-border bg-muted/30 p-5 text-center text-xs leading-relaxed text-muted-foreground">
        Educational only. Recovery Trauma™ is not a therapy service or medical
        provider. For urgent support see <Link to="/crisis" className="text-gold underline-offset-2 hover:underline">Crisis Resources</Link>.
      </p>
     </div>
    </main>
  );
}
